import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import PageTransition from "@/components/PageTransition";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FaUsers, FaComments } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { ChatForum } from "@/components/ChatForum";

// Message interface removed, handled by ChatForum

interface Room {
  id: string;
  name: string;
  description: string;
  icon: JSX.Element;
}

const rooms: Room[] = [
  {
    id: "general",
    name: "General Discussion",
    description: "Chat about anything related to misinformation and media literacy",
    icon: <FaComments className="h-5 w-5" />
  },
  {
    id: "news",
    name: "News Analysis",
    description: "Discuss and analyze current news stories",
    icon: <FaComments className="h-5 w-5" />
  },
  {
    id: "privacy",
    name: "Privacy & Security",
    description: "Share tips and discuss online privacy",
    icon: <FaComments className="h-5 w-5" />
  }
];

export default function Community() {
  const { currentUser } = useAuth();
  const [activeRoom, setActiveRoom] = useState<string>("general");
  // Chat-specific state, effects, and functions removed. Handled by ChatForum.

  if (!currentUser) {
    return (
      <PageTransition>
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <Card className="w-full max-w-md bg-black/40 border-truth/20">
            <CardHeader>
              <CardTitle className="text-center text-truth">Sign In Required</CardTitle>
            </CardHeader>
            <CardContent className="text-center text-gray-400">
              Please sign in to access the community features.
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-gray-900 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Rooms Sidebar */}
            <div className="w-full md:w-64 space-y-4">
              <div className="flex items-center gap-2 text-truth mb-4">
                <FaUsers className="h-6 w-6" />
                <h2 className="text-xl font-bold">Chat Rooms</h2>
              </div>
              <AnimatePresence>
                {rooms.map(room => (
                  <motion.button
                    key={room.id}
                    onClick={() => setActiveRoom(room.id)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 24 }}
                    className={`w-full text-left p-4 rounded-2xl shadow-lg transition-all duration-300 border-2
                      ${activeRoom === room.id 
                        ? 'bg-gradient-to-r from-truth/30 via-blue-900/40 to-black/60 text-truth border-truth/60 scale-105' 
                        : 'bg-black/40 text-gray-300 border-transparent hover:bg-truth/10 hover:text-truth hover:border-truth/30 hover:scale-105'}`}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-3">
                      {room.icon}
                      <div>
                        <h3 className="font-semibold text-lg">{room.name}</h3>
                        <p className="text-sm text-gray-400">{room.description}</p>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </div>

            {/* Chat Area */}
            <div className="flex-1">
              <Card className="bg-black/40 border-truth/20 h-[calc(100vh-12rem)]">
                <CardHeader className="border-b border-truth/20">
                  <CardTitle className="text-truth">
                    {rooms.find(r => r.id === activeRoom)?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 flex flex-col h-full">
                  <ChatForum activeRoomId={activeRoom} />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
